"use client";

import type { Segment, TranscriptionResponse } from "@ui/components/demos/STT/types";
import type { STTHeroSample } from "./useSTTHeroPlayback";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchSTTSampleTranscript } from "@ui/components/demos/STT/STTServerFn";
import { convertSampleResponseToSegments } from "@ui/components/demos/STT/utils";
import { logger } from "@utilities/logger";

const getSampleSegments = (sample: STTHeroSample | undefined, loadedSegments: Record<string, Segment[]>) => {
  if (!sample) return [];
  if (sample.segments?.length) return sample.segments;
  if (loadedSegments[sample.id]) return loadedSegments[sample.id];
  if (!sample.sttResponse) return [];

  try {
    const parsed: TranscriptionResponse =
      typeof sample.sttResponse === "string" ? JSON.parse(sample.sttResponse) : sample.sttResponse;
    return convertSampleResponseToSegments(parsed);
  } catch {
    return [];
  }
};

export function useSTTHeroSampleSegments(samples: STTHeroSample[], selectedIndex: number) {
  const [loadedSegments, setLoadedSegments] = useState<Record<string, Segment[]>>({});
  const [loadingSampleId, setLoadingSampleId] = useState<string | null>(null);
  const [transcriptFetchCompletedBySampleId, setTranscriptFetchCompletedBySampleId] = useState<Record<string, boolean>>(
    {},
  );
  const loadingSampleIdRef = useRef<string | null>(null);
  // Kept as a ref so loadSegmentsIfNeeded can guard against double-fetches without
  // taking loadedSegments as a dep and recreating on every segment load.
  const loadedSegmentsRef = useRef(loadedSegments);
  loadedSegmentsRef.current = loadedSegments;

  const currentSample = samples[selectedIndex];
  const sampleSegments = useMemo(
    () => getSampleSegments(currentSample, loadedSegments),
    [currentSample, loadedSegments],
  );

  const loadSegmentsIfNeeded = useCallback(
    async (sample: STTHeroSample) => {
      if (sample.segments?.length || sample.sttResponse || loadedSegmentsRef.current[sample.id]) return;

      loadingSampleIdRef.current = sample.id;
      setLoadingSampleId(sample.id);
      try {
        const transcript = await fetchSTTSampleTranscript(sample.id);
        if (transcript?.segments) {
          setLoadedSegments(prev => ({ ...prev, [sample.id]: transcript.segments }));
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "[useSTTHeroPlayback] fetchSTTSampleTranscript failed");
      } finally {
        if (loadingSampleIdRef.current === sample.id) {
          loadingSampleIdRef.current = null;
          setLoadingSampleId(null);
        }
        setTranscriptFetchCompletedBySampleId(prev => ({ ...prev, [sample.id]: true }));
      }
    },
    // loadedSegmentsRef.current is read at call-time; no dep needed.

    [],
  );

  useEffect(() => {
    if (samples[0]) loadSegmentsIfNeeded(samples[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sampleNeedsFetchedTranscript = !!currentSample && !currentSample.segments?.length && !currentSample.sttResponse;
  const isSampleLoading =
    sampleSegments.length === 0 &&
    sampleNeedsFetchedTranscript &&
    currentSample != null &&
    (!transcriptFetchCompletedBySampleId[currentSample.id] || loadingSampleId === currentSample.id);

  return {
    currentSample,
    sampleSegments,
    isSampleLoading,
    loadSegmentsIfNeeded,
  };
}
